import { FC, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanyProfile } from 'common/hooks/useCompanyProfile';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import ModalProvider from 'components/ModalProvider';

import Documents from './pages/BankStatements';
import Estimation from './pages/Estimation';
import EstimationSettingsContainer from './pages/Estimation/components/EstimationSettingsContainer';
import MicroEstimation from './pages/Estimation/flows/microEstimation';
import { SelectedTaxForecastProvider } from './pages/Estimation/hooks/useSelectedTaxForecastPeriod';
import ExportOneOff from './pages/ExportOneOff';
import ExportRecurring from './pages/ExportRecurring';
import ExportsHome from './pages/ExportsHome';
import { NewExportOneOff } from './pages/NewExportOneOff';
import { NewExportRecurring } from './pages/NewExportRecurring';
import { NewExportsHome } from './pages/NewExportsHome';
import RecurringExportDetails from './pages/RecurringExportDetails';
import * as routes from './routes';

const Accounting: FC = () => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { companyProfileId } = useCompanyContext();
  const { company } = useCompanyProfile(companyProfileId);
  const { featureFlags } = useFeatureFlagsSwitcher();

  const canAccessTaxForecasts =
    canAccessFeature({
      name: FeatureName.BankTaxforecasts,
      withChildFeatureOnly: true,
    }) && company?.isMicro;

  const defaultRoute = useMemo(() => {
    return canAccessTaxForecasts ? routes.estimation : routes.exports;
  }, [canAccessTaxForecasts]);

  return (
    <SelectedTaxForecastProvider>
      <Switch>
        <Route
          component={
            featureFlags.enableNewAccountingExport
              ? NewExportsHome
              : ExportsHome
          }
          exact
          path={routes.exports}
        />

        <Route
          component={canAccessTaxForecasts ? Estimation : () => null}
          exact
          path={routes.estimation}
        />

        <Route
          component={
            canAccessTaxForecasts ? EstimationSettingsContainer : () => null
          }
          path={routes.estimationSettings}
        />

        <Route
          component={canAccessTaxForecasts ? MicroEstimation : () => null}
          path={routes.microEstimationFlow}
        />

        <Route exact path={routes.exportDetails}>
          <ModalProvider>
            <RecurringExportDetails />
          </ModalProvider>
        </Route>

        <Route
          component={
            featureFlags.enableNewAccountingExport
              ? NewExportOneOff
              : ExportOneOff
          }
          path={routes.newExportOneOff}
        />
        <Route
          component={
            featureFlags.enableNewAccountingExport
              ? NewExportRecurring
              : ExportRecurring
          }
          path={routes.newExportRecurring}
        />

        {canAccessFeature({ name: FeatureName.BankInfo }) ? (
          <Route component={Documents} path={routes.documents} />
        ) : null}

        <Redirect to={defaultRoute} />
      </Switch>
    </SelectedTaxForecastProvider>
  );
};

export default Accounting;
